import { useState, useEffect, useMemo } from 'react';
import { useUser } from '../hooks/useUser';
import { fetchProducts } from '../network/fetchProducts';

import { default as Button } from 'antd/es/button';
import { default as Table, type TableProps } from 'antd/es/table';
import { FormattedProduct } from '../utils/FormattedProduct';
import { Resource } from '../components/Resource';
import { goToCustomerApp } from '../utils/goToCustomerApp';
import { ProductFromNetwork } from '../data/createPayoutData/types';
import { generateFilters } from '../utils/antd/tableFilter';
import { Link } from 'react-router-dom';
import { enhanceLinkWithToken } from '../utils/enhanceLinkWithToken';

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

export function ProductsPage({
  origin
}: {
  origin: 'disbursement' | 'collection';
}) {
  return (
    <div>
      {origin === 'disbursement' && (
        <Resource resource={{ filePath: 'api/products' }} />
      )}

      {origin === 'collection' && (
        <Resource resource={{ filePath: 'api/collection-products' }} />
      )}
      <ProductsPageInner origin={origin} />
    </div>
  );
}

export function ProductsPageInner({
  origin
}: {
  origin: 'disbursement' | 'collection';
}) {
  const [products, setProducts] = useState<ProductFromNetwork[]>([]);

  const [loadingProducts, setLoadingProducts] = useState(false);

  const { currentOrganisation } = useUser();

  useEffect(() => {
    const getProducts = async () => {
      setLoadingProducts(true);

      if (!currentOrganisation) {
        setLoadingProducts(false);

        return;
      }

      try {
        const products = await fetchProducts(
          currentOrganisation.id.toString(),
          origin
        );

        if (products?.data) {
          setProducts(products?.data);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoadingProducts(false);
      }
    };

    getProducts();
  }, [currentOrganisation, origin]);

  const columns: TableProps<ProductFromNetwork>['columns'] = useMemo(
    () =>
      [
        {
          title: 'COUNTRY',
          dataIndex: 'country_iso',
          width: '20%',
          key: 'country_iso',
          render: (iso_code) => (
            <FormattedProduct
              label={regionNames.of(iso_code) ?? ''}
              countryIso={iso_code}
            />
          ),
          ...generateFilters(products, 'country_iso')
        },
        {
          title: 'CURRENCY',
          width: '20%',
          dataIndex: 'currency_iso',
          key: 'currency_iso',
          ...generateFilters(products, 'currency_iso')
        },
        {
          title: 'INSTRUMENT',
          width: '20%',
          dataIndex: 'instrument',
          key: 'instrument',
          ...generateFilters(products, 'instrument')
        },
        {
          title: 'EXAMPLE',
          hidden: origin !== 'disbursement',
          width: '20%',
          dataIndex: 'id',
          key: 'examples',
          render: (id: string) => {
            return (
              <Button>
                <Link
                  to={enhanceLinkWithToken(
                    `/reference/payouts#payouts-create?product_id=${id}`
                  )}
                >
                  See example
                </Link>
              </Button>
            );
          }
        }
      ].filter((item) => !item.hidden),
    [origin, products]
  );

  return !currentOrganisation ? (
    <div className="flex">
      <Button
        data-testid="log-in-from-products"
        onClick={() => goToCustomerApp()}
        type="primary"
      >
        Log in to see your available products
      </Button>
    </div>
  ) : (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={products}
      loading={loadingProducts}
      size="small"
    />
  );
}
